import { useEffect } from 'react';
import useIsInIframe from './useIsInIframe';

/**
* Custom hook to hide scrollbars when in iframe mode
* Adds/removes the 'iframe-mode' class to html and body elements
*/
const useIframeScrollbar = () => {
  const isInIframe = useIsInIframe();

  useEffect(() => {
    let resizeObserver = null;
    let mutationObserver = null;
    let rafId = null;
    const HEIGHT_BUFFER = 0.975;
    const BOTTOM_RESERVE_PX = 24;
    const HOME_WIDTH_FIT_TARGET_PX = 1200;
    const HOME_WIDTH_FIT_MAX_VIEWPORT_PX = 1280;
    const getViewportWidth = (frameElement) => {
      const frameRectWidth = frameElement?.getBoundingClientRect().width || 0;
      const frameClientWidth = frameElement?.clientWidth || 0;
      return (
        frameRectWidth ||
        frameClientWidth ||
        window.innerWidth ||
        document.documentElement.clientWidth ||
        0
      );
    };

    const scheduleFitUpdate = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        if (!isInIframe) {
          return;
        }

        const body = document.body;
        const html = document.documentElement;
        const frameElement =
          window.frameElement instanceof HTMLElement ? window.frameElement : null;

        const frameRectHeight = frameElement?.getBoundingClientRect().height || 0;
        const frameClientHeight = frameElement?.clientHeight || 0;
        const frameAttributeHeight = parseFloat(
          String(frameElement?.getAttribute('height') || '').replace(/[^\d.]/g, '')
        ) || 0;
        const viewportHeight =
          frameRectHeight ||
          frameClientHeight ||
          window.innerHeight ||
          document.documentElement.clientHeight ||
          0;

        if (!viewportHeight || !body || !html) {
          return;
        }

        const contentHeight = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          html.scrollHeight,
          html.offsetHeight
        );
        const roundedHeight = Math.ceil(contentHeight);

        try {
          window.parent?.postMessage(
            {
              type: 'IFRAME_HEIGHT',
              source: 'mortgage-tool',
              height: roundedHeight,
            },
            '*'
          );
        } catch (error) {
          // Ignore cross-origin messaging failures.
        }

        // Use the larger of rendered content height and iframe declared height.
        // This handles embeds like: parent 800px + iframe height attribute 1200px => scale 800/1200.
        const iframeReferenceHeight = Math.max(
          contentHeight + BOTTOM_RESERVE_PX,
          frameAttributeHeight,
          1
        );
        const computedHeightScale = Math.min(
          1,
          (viewportHeight / iframeReferenceHeight) * HEIGHT_BUFFER
        );
        const viewportWidth = getViewportWidth(frameElement);
        const isHomePage =
          body.classList.contains('home-page') || html.classList.contains('home-page');
        const shouldApplyHomeWidthFit =
          isHomePage &&
          viewportWidth > 0 &&
          viewportWidth <= HOME_WIDTH_FIT_MAX_VIEWPORT_PX;
        const computedWidthScale = shouldApplyHomeWidthFit
          ? Math.min(1, viewportWidth / HOME_WIDTH_FIT_TARGET_PX)
          : 1;
        const computedScale = Math.min(computedHeightScale, computedWidthScale);
        const fitScale = Math.max(0.1, computedScale);
        const hasScaleReduction = fitScale < 0.999;

        html.style.setProperty('--iframe-content-scale', fitScale.toFixed(4));
        body.style.setProperty('--iframe-content-scale', fitScale.toFixed(4));
        html.classList.toggle('iframe-content-scaled', hasScaleReduction);
        body.classList.toggle('iframe-content-scaled', hasScaleReduction);

        if (hasScaleReduction) {
          const targetContainer = document.querySelector(
            '.form-containers, .section, .psection'
          );

          if (targetContainer instanceof HTMLElement && viewportWidth > 0) {
            const rect = targetContainer.getBoundingClientRect();
            const viewportCenter = viewportWidth / 2;
            const contentCenter = rect.left + rect.width / 2;
            const currentOffset = parseFloat(
              html.style.getPropertyValue('--iframe-content-offset-x') || '0'
            ) || 0;
            const centerDelta = viewportCenter - contentCenter;
            const nextOffset = currentOffset + centerDelta;
            const safeOffset = Number.isFinite(nextOffset) ? nextOffset : 0;

            html.style.setProperty('--iframe-content-offset-x', `${safeOffset.toFixed(2)}px`);
            body.style.setProperty('--iframe-content-offset-x', `${safeOffset.toFixed(2)}px`);
          }
        } else {
          html.style.setProperty('--iframe-content-offset-x', '0px');
          body.style.setProperty('--iframe-content-offset-x', '0px');
        }
      });
    };

    if (isInIframe) {
      document.body.classList.add('iframe-mode');
      document.documentElement.classList.add('iframe-mode');
      scheduleFitUpdate();

      window.addEventListener('resize', scheduleFitUpdate);
      window.addEventListener('orientationchange', scheduleFitUpdate);
      window.addEventListener('load', scheduleFitUpdate);

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          scheduleFitUpdate();
        });
        resizeObserver.observe(document.body);
      }

      if (typeof MutationObserver !== 'undefined') {
        mutationObserver = new MutationObserver(() => {
          scheduleFitUpdate();
        });
        mutationObserver.observe(document.body, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
        });
      }
    } else {
      document.body.classList.remove('iframe-mode');
      document.documentElement.classList.remove('iframe-mode');
      document.body.classList.remove('iframe-content-scaled');
      document.documentElement.classList.remove('iframe-content-scaled');
      document.body.style.removeProperty('--iframe-content-scale');
      document.documentElement.style.removeProperty('--iframe-content-scale');
      document.body.style.removeProperty('--iframe-content-offset-x');
      document.documentElement.style.removeProperty('--iframe-content-offset-x');
    }

    // Cleanup function to remove class when component unmounts
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      window.removeEventListener('resize', scheduleFitUpdate);
      window.removeEventListener('orientationchange', scheduleFitUpdate);
      window.removeEventListener('load', scheduleFitUpdate);

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      if (mutationObserver) {
        mutationObserver.disconnect();
      }

      document.body.classList.remove('iframe-mode');
      document.documentElement.classList.remove('iframe-mode');
      document.body.classList.remove('iframe-content-scaled');
      document.documentElement.classList.remove('iframe-content-scaled');
      document.body.style.removeProperty('--iframe-content-scale');
      document.documentElement.style.removeProperty('--iframe-content-scale');
      document.body.style.removeProperty('--iframe-content-offset-x');
      document.documentElement.style.removeProperty('--iframe-content-offset-x');
    };
  }, [isInIframe]);
};

export default useIframeScrollbar;

 